import { useQuery } from '@tanstack/react-query';
import type { Feature, FeatureCollection, Geometry } from 'geojson';

/**
 * Lazy loader for the Norwegian municipality geometry (2024). The TopoJSON (~85 KB,
 * committed static data) and topojson-client are dynamically imported, so the
 * Marked list stays fast and the geometry is only fetched once the map is opened.
 * React Query keeps the decoded features in memory for the session.
 */
export interface KommuneProps {
  kommunenummer: string;
  name: string;
}
export type KommuneFeature = Feature<Geometry, KommuneProps>;

/** The kommunenummer for a feature (topology id, with a property fallback). */
export function kommuneCode(f: KommuneFeature): string {
  return String(f.id ?? f.properties.kommunenummer);
}

export function useKommuner() {
  return useQuery({
    queryKey: ['kommuner-topo-2024'],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async (): Promise<KommuneFeature[]> => {
      const [{ feature }, mod] = await Promise.all([
        import('topojson-client'),
        import('./kommuner-2024.topo.json'),
      ]);
      const topo = ((mod as { default?: unknown }).default ?? mod) as Parameters<typeof feature>[0];
      const collection = feature(
        topo,
        (topo as { objects: Record<string, unknown> }).objects.kommuner as never,
      ) as unknown as FeatureCollection<Geometry, KommuneProps>;
      return collection.features;
    },
  });
}
